import { useContentDeliveryServiceV3 } from "@rw1/api-services";
import { ContentDelivery_V3 } from "@rw1/auto-generated-clients";
import { useMemo } from "react";
import { useCompaniesData } from "./useCompaniesData";
import { useAllContentMetadata } from "./useContentMetadata";
import { useFetchAll } from "./useFetchAll";

export const ALL_RELEASE_CHANNELS_QUERY_KEY = "allReleaseChannels";
export const LIVE_RELEASE_CHANNEL_NAME = "live";

type RawReleaseChannel = ContentDelivery_V3.ReleaseChannelWithUsersCount & {
  companyId?: string;
  viewerCompanyId?: string;
  blockedSeats?: number | null;
  expirationDate?: string | null;
  licenseStartDate?: string | null;
  version?: string | null;
  cardImageUrl?: string | null;
  [key: string]: unknown;
};

export interface Experience {
  releaseChannelId: string;
  contentMetadataId: string;
  releaseChannelName: string;
  companyId: string;
  companyName?: string;
  friendlyName: string;
  viewerCompanyId?: string;
  seatsTotal?: number | null;
  seatsUsed?: number;
  blockedSeats?: number | null;
  expirationDate?: string | null;
  licenseStartDate?: string | null;
  version?: string | null;
  description?: string | null;
  cardImageUrl?: string | null;
}

export interface UseAllReleaseChannelsOptions {
  pageSize?: number;
  contentMetadataIds?: string[];
  releaseChannelIds?: string[];
  releaseChannelName?: string;
  enabled?: boolean;
  includeBinaries?: boolean;
}

export interface UseAllReleaseChannelsResult {
  experiences: Experience[];
  experiencesMap: Map<string, Experience>;
  isLoading: boolean;
  error: Error | null;
}

// Helper for safe metadata string extraction
const getMetaStr = (meta: unknown, ...keys: string[]): string | undefined => {
  if (!meta || typeof meta !== "object") return undefined;
  const record = meta as Record<string, unknown>;
  for (const key of keys) {
    if (typeof record[key] === "string") return record[key];
  }

  return undefined;
};

export const useAllExperiences = (
  options: UseAllReleaseChannelsOptions = {},
): UseAllReleaseChannelsResult => {
  const {
    pageSize = 1000,
    contentMetadataIds,
    releaseChannelIds,
    releaseChannelName = LIVE_RELEASE_CHANNEL_NAME,
    enabled = true,
    includeBinaries = false,
  } = options;

  const contentDeliveryClient = useContentDeliveryServiceV3();

  const {
    data: rawExperiences,
    isLoading: isExperiencesLoading,
    error,
  } = useFetchAll<RawReleaseChannel>(
    [
      ALL_RELEASE_CHANNELS_QUERY_KEY,
      contentMetadataIds,
      releaseChannelIds,
      releaseChannelName,
      pageSize,
    ],
    async (page, size) => {
      const req = new ContentDelivery_V3.ReleaseChannelSearchRequest({
        pageNumber: page,
        pageSize: size,
        contentMetadataIds,
        releaseChannelIds,
        releaseChannelName,
        includeUsersCount: true,
      });

      return (await contentDeliveryClient.searchReleaseChannels(
        req,
      )) as RawReleaseChannel[];
    },
    { enabled, pageSize },
  );

  const { contentMetadataMap, isLoading: isContentMetadataLoading } =
    useAllContentMetadata({
      enabled,
      includeBinaries,
    });

  const { companyNamesByIdMap, isLoading: isCompaniesDataLoading } =
    useCompaniesData();

  const experiences = useMemo<Experience[]>(() => {
    if (!rawExperiences) return [];

    if (enabled && isContentMetadataLoading && contentMetadataMap.size === 0) {
      return [];
    }

    return rawExperiences.map((rc) => {
      const meta = contentMetadataMap.get(rc.contentMetadataId);

      const companyId = rc.companyId ?? getMetaStr(meta, "companyId") ?? "";
      const companyName = companyNamesByIdMap[companyId];

      const friendlyName =
        getMetaStr(meta, "friendlyName", "FriendlyName") ??
        rc.releaseChannelName;

      const description =
        getMetaStr(meta, "description", "Description") ?? null;

      return {
        releaseChannelId: rc.releaseChannelId,
        contentMetadataId: rc.contentMetadataId,
        releaseChannelName: rc.releaseChannelName,
        friendlyName,
        companyId,
        companyName,
        viewerCompanyId: rc.viewerCompanyId,
        seatsTotal: null,
        seatsUsed: rc.usersCount,
        blockedSeats: rc.blockedSeats ?? null,
        expirationDate: rc.expirationDate ?? null,
        licenseStartDate: rc.licenseStartDate ?? null,
        version: rc.version ?? null,
        cardImageUrl: rc.cardImageUrl ?? null,
        description,
      };
    });
  }, [
    rawExperiences,
    contentMetadataMap,
    companyNamesByIdMap,
    enabled,
    isContentMetadataLoading,
  ]);

  const experiencesMap = useMemo(() => {
    const map = new Map<string, Experience>();
    for (const e of experiences) {
      map.set(e.releaseChannelId, e);
    }

    return map;
  }, [experiences]);

  const combinedLoading =
    isExperiencesLoading ||
    (enabled && isContentMetadataLoading) ||
    isCompaniesDataLoading;

  return useMemo(
    () => ({
      experiences,
      experiencesMap,
      isLoading: combinedLoading,
      error,
    }),
    [experiences, experiencesMap, combinedLoading, error],
  );
};
